import React, { useMemo, Fragment } from 'react';
import Localize from 'react-intl-universal';

import { Formik, Form, FieldArray } from 'formik';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import { formatDateTimeToMDYSlashDelimiter } from '@common/Util';
import CreateFieldsForArrayFromConfig from '@components/CreateFormFields/CreateFieldsForArrayFromConfig';
import CreateFieldsFromConfig from '@components/CreateFormFields/CreateFieldsFromConfig';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { bookingRegions } from '@pages/BookingProcess/AdditionalInformation/mockedData';

import emptyValues from './emptyValues';
import initialValues from './initialValues';
import useCreateSchema from './useCreateSchema';
import {
  organizationalDataConfig,
  informationConfig,
  emailConfig,
  addressConfig,
  communicationConfig
} from '../../config';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '1rem'
}));

const StyledGrid = styled(Grid)(() => ({
  '&.MuiGrid-root': {
    marginTop: '0.1rem'
  }
}));

const StyledGridItem = styled(Grid)(() => ({
  marginBottom: '0.8rem',
  '&.MuiGrid-root': {
    paddingTop: '0.1rem'
  }
}));

const StyledButton = styled(Button)(() => ({
  textTransform: 'none',
  fontSize: '14px',
  '& .MuiButton-startIcon ': {
    marginRight: 0
  }
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '16px',
  textTransform: 'none',
  height: '2rem',
  color: theme.palette.action.main
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  marginTop: '0.5rem',
  paddingTop: 0,
  fontSize: '20px',
  color: theme.palette.booking.header
}));

const DetailsForm = () => {
  const validationSchema = useCreateSchema();
  const [contextData] = useGlobalContext();

  const { country: countryOptions, title: titleOptions } = contextData?.bookingFormData || {};

  const onSubmit = () => {};

  const regionOptions = useMemo(
    () =>
      bookingRegions.map((region) => ({
        ...region,
        value: Localize.get(region.value, '')
      })),
    []
  );
  const optionsObj = {
    titleOptions,
    regionOptions,
    countryOptions
  };

  const handleFields = {
    ['information.birthDate']: (setFieldValue) => ({
      onChange: (newValue) =>
        setFieldValue('information.birthDate', formatDateTimeToMDYSlashDelimiter(newValue))
    })
  };

  const removeItem = (remove, values, arrayName) => {
    if (values?.[arrayName]?.length > 1) {
      remove(values[arrayName].length - 1);
    }
  };

  return (
    <StyledBox sx={{ width: '100%' }}>
      <Formik
        validateOnMount
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, setValues, setFieldValue }) => (
          <Form>
            <Box sx={{ display: 'flex', mt: 1, justifyContent: 'end' }}>
              <StyledSubmitButton onClick={() => setValues(initialValues)}>
                {Localize.get('MyProfile.dismiss')}
              </StyledSubmitButton>
              <Divider orientation='vertical' flexItem />
              <StyledSubmitButton type='submit'>
                {Localize.get('MyProfile.saveChanges')}
              </StyledSubmitButton>
            </Box>
            <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
              <StyledGridItem item xs={12} sm={12}>
                <StyledHeader>{Localize.get('MyProfile.information')}</StyledHeader>
              </StyledGridItem>
              <CreateFieldsFromConfig
                config={informationConfig}
                setFieldValue={setFieldValue}
                optionsObj={optionsObj}
                handleFields={handleFields}
              />
            </StyledGrid>
            <Divider sx={{ mt: 4, mb: 4 }} />

            <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
              <StyledGridItem item xs={12} sm={12}>
                <StyledHeader>{Localize.get('MyProfile.organizationalData')}</StyledHeader>
              </StyledGridItem>
              <CreateFieldsFromConfig config={organizationalDataConfig} />
            </StyledGrid>
            <Divider sx={{ mt: 4, mb: 4 }} />

            <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
              <StyledGridItem item xs={12} sm={12}>
                <StyledHeader>{Localize.get('Booking.email')}</StyledHeader>
              </StyledGridItem>

              <FieldArray name='emails'>
                {({ remove, push }) => (
                  <>
                    {values.emails.length > 0 &&
                      values.emails.map((email, index) => (
                        <Fragment key={index}>
                          <CreateFieldsForArrayFromConfig
                            config={emailConfig}
                            arrayIndex={index}
                            fieldsArrayName={'emails'}
                            optionsObj={optionsObj}
                          />
                        </Fragment>
                      ))}
                    <StyledGridItem item xs={12} md={12}>
                      <StyledButton
                        startIcon={<AddIcon />}
                        onClick={() => push(emptyValues?.emails)}
                      >
                        {Localize.get('MyProfile.addEmail')}
                      </StyledButton>
                      <StyledButton
                        startIcon={<RemoveIcon />}
                        disabled={values?.emails?.length === 1}
                        onClick={() => removeItem(remove, values, 'emails')}
                      >
                        {Localize.get('MyProfile.removeEmail')}
                      </StyledButton>
                    </StyledGridItem>
                  </>
                )}
              </FieldArray>
            </StyledGrid>
            <Divider sx={{ mt: 4, mb: 4 }} />

            <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
              <StyledGridItem item xs={12} sm={12}>
                <StyledHeader>{Localize.get('EventDetails.address')}</StyledHeader>
              </StyledGridItem>

              <FieldArray name='addresses'>
                {({ remove, push }) => (
                  <>
                    {values.addresses.length > 0 &&
                      values.addresses.map((address, index) => (
                        <Fragment key={index}>
                          <CreateFieldsForArrayFromConfig
                            config={addressConfig}
                            arrayIndex={index}
                            fieldsArrayName={'addresses'}
                            optionsObj={optionsObj}
                          />
                        </Fragment>
                      ))}
                    <StyledGridItem item xs={12} md={12}>
                      <StyledButton
                        startIcon={<AddIcon />}
                        onClick={() => push(emptyValues?.address)}
                      >
                        {Localize.get('MyProfile.addAddress')}
                      </StyledButton>
                      <StyledButton
                        disabled={values?.addresses?.length === 1}
                        startIcon={<RemoveIcon />}
                        onClick={() => removeItem(remove, values, 'addresses')}
                      >
                        {Localize.get('MyProfile.removeAddress')}
                      </StyledButton>
                    </StyledGridItem>
                  </>
                )}
              </FieldArray>
            </StyledGrid>
            <Divider sx={{ mt: 4, mb: 4 }} />

            <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
              <StyledGridItem item xs={12} sm={12}>
                <StyledHeader>{Localize.get('MyProfile.communication')}</StyledHeader>
              </StyledGridItem>

              <FieldArray name='communications'>
                {({ remove, push }) => (
                  <>
                    {values.communications.length > 0 &&
                      values.communications.map((communication, index) => (
                        <Fragment key={index}>
                          <CreateFieldsForArrayFromConfig
                            config={communicationConfig}
                            arrayIndex={index}
                            fieldsArrayName={'communications'}
                            optionsObj={optionsObj}
                          />
                        </Fragment>
                      ))}
                    <StyledGridItem item xs={12} md={12}>
                      <StyledButton
                        startIcon={<AddIcon />}
                        onClick={() => push(emptyValues?.communication)}
                      >
                        {Localize.get('MyProfile.addAPhone')}
                      </StyledButton>
                      <StyledButton
                        disabled={values?.communications?.length === 1}
                        startIcon={<RemoveIcon />}
                        onClick={() => removeItem(remove, values, 'communications')}
                      >
                        {Localize.get('MyProfile.removePhone')}
                      </StyledButton>
                    </StyledGridItem>
                  </>
                )}
              </FieldArray>
            </StyledGrid>
          </Form>
        )}
      </Formik>
    </StyledBox>
  );
};
export default DetailsForm;
